/**
 * Box vs block-shape collision.
 *
 * Blocks collide as their own shape, not as a whole cell (see `collisionOf` in
 * blocks.ts): a bed is a 9/16-tall mattress slab, a chest 14x14x14. The player
 * and the primed-TNT entity sweep a box through the same shapes, so the sweep
 * itself lives here.
 *
 * A box is six numbers in world units: minX, minY, minZ, maxX, maxY, maxZ.
 * Shapes come from a per-cell callback in 1/16 block units (x0,y0,z0,x1,y1,z1
 * per box), which is also where each caller decides what lies outside the
 * world.
 */

/** Collision boxes of the block in a cell, in 1/16 units. */
export type ShapesAt = (x: number, y: number, z: number) => Float32Array;

/**
 * Largest part of a move of `d` along `axis` (0=x, 1=y, 2=z) the box can make
 * before hitting a block's collision shape — vanilla's `VoxelShape.collideX`.
 *
 * The nearest shape *edge* in the direction of travel wins, whether or not the
 * box already overlaps that shape: a box resting flush on the ground (its floor
 * face exactly on the block top) is met by an edge at distance 0 and may not
 * sink in, while a box standing inside geometry has no edge ahead of it and can
 * still be moved out. `eps` is vanilla's 1e-7 tolerance on both.
 */
export function clipMove(box: number[], axis: number, d: number, eps: number, shapesAt: ShapesAt): number {
  if (d === 0) return 0;
  const b = (axis + 1) % 3;
  const c = (axis + 2) % 3;
  const face = d > 0 ? box[axis + 3] : box[axis];
  let best = d;
  // Cells the swept box can reach: its extent along the axis widens by `d` on
  // the side it travels towards, plus one cell of slack either side.
  const lo = Math.floor(box[axis] + Math.min(d, 0)) - 1;
  const hi = Math.floor(box[axis + 3] + Math.max(d, 0)) + 1;
  // the cell below the box too: fences and walls are 1.5 tall and reach into the cell above them
  const b0 = Math.floor(box[b] + eps) - (b === 1 ? 1 : 0);
  const b1 = Math.floor(box[b + 3] - eps);
  const c0 = Math.floor(box[c] + eps) - (c === 1 ? 1 : 0);
  const c1 = Math.floor(box[c + 3] - eps);
  const cell = [0, 0, 0];
  for (let j = b0; j <= b1; j++) {
    for (let k = c0; k <= c1; k++) {
      cell[b] = j;
      cell[c] = k;
      for (let i = lo; i <= hi; i++) {
        cell[axis] = i;
        const shapes = shapesAt(cell[0], cell[1], cell[2]);
        for (let s = 0; s < shapes.length; s += 6) {
          // the shape has to overlap the box across the two other axes
          if (shapes[s + b] / 16 + j >= box[b + 3] || shapes[s + b + 3] / 16 + j <= box[b]) continue;
          if (shapes[s + c] / 16 + k >= box[c + 3] || shapes[s + c + 3] / 16 + k <= box[c]) continue;
          if (d > 0) {
            const dist = shapes[s + axis] / 16 + i - face;
            if (dist >= -eps && dist < best) best = dist;
          } else {
            const dist = shapes[s + axis + 3] / 16 + i - face;
            if (dist <= eps && dist > best) best = dist;
          }
        }
      }
    }
  }
  return best;
}

/** Does any block's collision shape overlap the box? */
export function boxHits(box: readonly number[], eps: number, shapesAt: ShapesAt): boolean {
  const x0 = Math.floor(box[0] + eps);
  const x1 = Math.floor(box[3] - eps);
  const y0 = Math.floor(box[1] + eps) - 1; // cell below: tall shapes (fences) reach up into this one
  const y1 = Math.floor(box[4] - eps);
  const z0 = Math.floor(box[2] + eps);
  const z1 = Math.floor(box[5] - eps);
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const shapes = shapesAt(x, y, z);
        for (let s = 0; s < shapes.length; s += 6) {
          if (shapes[s] / 16 + x < box[3] && shapes[s + 3] / 16 + x > box[0]
            && shapes[s + 1] / 16 + y < box[4] && shapes[s + 4] / 16 + y > box[1]
            && shapes[s + 2] / 16 + z < box[5] && shapes[s + 5] / 16 + z > box[2]) return true;
        }
      }
    }
  }
  return false;
}
